/**
 * Utility function to extract mentions from text and convert them to user IDs
 * Based on the logic from mentionHighlight.tsx
 */

type MentionMatch = {
  mention: string;
  authorId?: string;
  authorName: string;
};

/**
 * Extract mentions from text using similar logic to renderTextWithMentions
 */
export const extractMentionsFromText = (
  text: string,
  mentionMap: Map<string, string> = new Map(), // Map from author name to authorId
): string[] => {
  const matches: string[] = [];
  // Convert mentionMap to the same format as renderTextWithMentions expects
  const mentionNames = Array.from(mentionMap.keys());
  // Create @mention patterns without adding space
  const mentionsWithAt = mentionNames.map((mention) => (mention.startsWith("@") ? mention : `@${mention}`));

  if (mentionsWithAt.length === 0) {
    return matches;
  }

  // Match @mention followed by word boundary, space, or end of string
  const mentionRegex = new RegExp(
    `(${mentionsWithAt.map((m) => m.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})(?=\\s|$|[^\\w])`,
    "g",
  );

  let match: RegExpExecArray | null;
  match = mentionRegex.exec(text);
  while (match !== null) {
    const foundMention = match[1];

    // Find the original mention name without @
    const cleanMention = foundMention.replace(/^@/, "");
    const authorId = mentionMap.get(cleanMention);
    if (authorId) {
      matches.push(authorId);
    }
    match = mentionRegex.exec(text);
  }

  return [...new Set(matches)]; // Remove duplicates
};

/**
 * Create mention-to-ID mapping for a specific comment context
 */
export const createMentionMap = (mentions: MentionMatch[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const { authorName, authorId } of mentions) {
    if (authorId) {
      map.set(authorName, authorId);
    }
  }
  return map;
};

/**
 * Convert @mentions in text to <@userId> format (Slack-style)
 */
export const convertMentionsToUserIds = (text: string, mentionMap: Map<string, string> = new Map()): string => {
  let result = text;

  // Convert mentionMap to the same format as renderTextWithMentions expects
  const mentionNames = Array.from(mentionMap.keys());

  // Create @mention patterns without adding space
  const mentionsWithAt = mentionNames.map((mention) => (mention.startsWith("@") ? mention : `@${mention}`));

  if (mentionsWithAt.length === 0) {
    return result;
  }

  // Match @mention followed by word boundary, space, or end of string
  const allMentionsRegex = new RegExp(
    `(${mentionsWithAt.map((m) => m.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})(?=\\s|$|[^\\w])`,
    "g",
  );

  result = result.replace(allMentionsRegex, (match) => {
    // Find the original mention name without @
    const cleanMention = match.replace(/^@/, "");
    const authorId = mentionMap.get(cleanMention);
    return authorId ? `<@${authorId}>` : match; // Slack-style format
  });

  return result;
};

/**
 * Convert <@userId> format back to @userName for display
 */
export const convertUserIdsToMentions = (text: string, userIdToNameMap: Map<string, string> = new Map()): string => {
  // More flexible regex to match any characters except >
  const userIdRegex = /<@([^>]+)>/g;

  const result = text.replace(userIdRegex, (match, userId) => {
    const userName = userIdToNameMap.get(userId);
    return userName ? `@${userName} ` : match;
  });

  return result;
};

/**
 * Create user ID to name mapping from mentioned users
 */
export const createUserIdToNameMap = (
  mentionedUserIds: string[],
  userInfoMap: Map<string, { name: string; avatar?: string | null }> = new Map(),
): Map<string, string> => {
  const map = new Map<string, string>();
  for (const userId of mentionedUserIds) {
    const userInfo = userInfoMap.get(userId);
    if (userInfo) {
      map.set(userId, userInfo.name);
    }
  }
  return map;
};
